/**
 * Hook for fetching dynamic navigation items from the API.
 *
 * Returns sidebar navigation items, loading state, and error state.
 * Refetches when the route changes so newly installed/uninstalled
 * modules are reflected without a full page reload.
 *
 * Cross-References:
 *   - S-MOD.11: Frontend dynamic navigation
 *   - FR-UI-001: Dynamic nav item registration
 *   - api-specifications/foundation.md §10: Navigation endpoint
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { apiFetch } from '@/lib/api';
import { useAuthStore } from '@/stores/authStore';

/* ── Types ───────────────────────────────────────────────────── */

export interface NavItem {
  path: string;
  label: string;
  icon: string;
  section: 'main' | 'admin' | 'modules';
  sort_order: number;
  required_permission: string | null;
  module_name: string | null;
  /**
   * Sprint NAV-2 — Administration sub-group label. Only meaningful
   * when `section === "admin"`; null elsewhere. Module-contributed
   * admin rows default to their module name when the manifest does
   * not override.
   */
  admin_group: string | null;
  /**
   * Sprint NAV-3 — module-section sub-group label. Only meaningful
   * when `section === "modules"`; null elsewhere. Null = loose item
   * (renders at the top of the module section before any
   * sub-grouped buckets).
   */
  sub_group: string | null;
}

interface NavigationResponse {
  items: NavItem[];
}

interface UseNavigationResult {
  items: NavItem[];
  loading: boolean;
  error: boolean;
  /** Force-refetch — used by the Main-nav admin modal after save. */
  refetch: () => void;
}

/* ── Fallback items shown on error ───────────────────────────── */

const FALLBACK_ITEMS: NavItem[] = [
  {
    path: '/dashboard',
    label: 'Dashboard',
    icon: 'LayoutDashboard',
    section: 'main',
    sort_order: 0,
    required_permission: null,
    module_name: null,
    admin_group: null,
    sub_group: null,
  },
];

/* ── Latest-items snapshot ───────────────────────────────────── */

/**
 * ACT-1b — the most recent successfully fetched items, shared with
 * consumers that only need a point-in-time read (the visit recorder)
 * so they don't trigger another navigation fetch per route change.
 */
let latestItems: NavItem[] = [];
/** Whose menu `latestItems` is; logout doesn't reload the page. */
let latestItemsUserId: string | null = null;
export function navigationSnapshot(): readonly NavItem[] {
  return latestItems;
}

/* ── Hook ────────────────────────────────────────────────────── */

export function useNavigation(): UseNavigationResult {
  const userId = useAuthStore((s) => s.user?.id ?? null);
  // Start from this user's last menu so a remounted shell (each route
  // group mounts its own Layout) renders it at once, not a skeleton.
  const cached = userId !== null && userId === latestItemsUserId ? latestItems : [];
  const [items, setItems] = useState<NavItem[]>(cached);
  const [loading, setLoading] = useState(cached.length === 0);
  const [error, setError] = useState(false);
  const location = useLocation();
  const abortRef = useRef<AbortController | null>(null);

  const fetchNav = useCallback(async () => {
    // Cancel any in-flight request
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      setLoading(true);
      setError(false);
      const data = await apiFetch<NavigationResponse>(
        '/settings/navigation',
        { signal: controller.signal },
      );
      if (!controller.signal.aborted) {
        latestItems = data.items;
        latestItemsUserId = useAuthStore.getState().user?.id ?? null;
        setItems(data.items);
      }
    } catch (err: unknown) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      if (!controller.signal.aborted) {
        setError(true);
        setItems(FALLBACK_ITEMS);
      }
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
      }
    }
  }, []);

  // Fetch on mount and refetch on route change
  useEffect(() => {
    fetchNav();
    return () => abortRef.current?.abort();
  }, [fetchNav, location.pathname]);

  return { items, loading, error, refetch: fetchNav };
}
